import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'
import { LocationJson } from 'app/types/entities'
import { logError } from 'lib/logging'

export type SchedulingSubmissions = Record<
  string,
  {
    count: number
    last?: string
  }
>

export type SchedulingIndexLoaderData = {
  locations: {
    hits: LocationJson[]
    total: number
  }
  submissions: SchedulingSubmissions
}

export type UseSchedulingIndexReturn = {
  locations: {
    hits: LocationJson[]
    total: number
  }
  submissions: SchedulingSubmissions
  columns: ColumnDef<LocationJson, any>[]
}

export function useSchedulingIndex(
  getColumns: (submissions: SchedulingSubmissions) => ColumnDef<LocationJson, any>[],
): UseSchedulingIndexReturn {
  const [data, setData] = useState<SchedulingIndexLoaderData>({ locations: { hits: [], total: 0 }, submissions: {} })
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const params = new URLSearchParams()
    const sort = searchParams.get(`sort`)
    const query = searchParams.get(`query`)
    const page = searchParams.get(`page`)
    if (sort) params.set(`sort`, sort)
    if (query) params.set(`query`, query)
    if (page) params.set(`page`, page)
    fetch(`/api/scheduling/locations?${params.toString()}`, { credentials: `include` })
      .then((res) => res.json())
      .then((d) => setData(d))
      .catch((err) => logError(`Error fetching scheduling locations`, err))
  }, [searchParams])

  const columns = useMemo(() => getColumns(data.submissions), [data.submissions, getColumns])

  return {
    locations: data.locations,
    submissions: data.submissions,
    columns,
  }
}
